/**
 * Netzentgelte aus echten Preisblaettern
 *
 * Das Netzentgelt zahlt der Kunde an den oertlichen Netzbetreiber. Es ist
 * regional sehr unterschiedlich und wird jaehrlich als "Preisblatt"
 * veroeffentlicht. Zwei Tarifarten:
 *
 *   SLP (ohne Leistungsmessung)
 *     Arbeitspreis (ct/kWh) + Grundpreis (EUR/a) + Messstellenbetrieb (EUR/a)
 *
 *   RLM (registrierende Leistungsmessung, praktisch alle ab ~100.000 kWh)
 *     ZWEITEILIGER TARIF:
 *       Leistungspreis (EUR je kW Jahreshoechstleistung und Jahr)
 *     + Arbeitspreis (ct/kWh, deutlich niedriger als bei SLP)
 *     + Messstellenbetrieb (EUR/a)
 *     Dazu die BENUTZUNGSSTUNDENREGELUNG:
 *       Benutzungsstunden = Jahresverbrauch / Jahreshoechstleistung
 *       >= Grenze (ueblich 2.500 h/a) -> Hochlast-Tarif
 *          (hoher Leistungspreis, niedriger Arbeitspreis)
 *       <  Grenze -> Schwachlast-Tarif (umgekehrt)
 *
 * EHRLICHKEIT IST HIER PFLICHT (Grundregel 1: niemals Zahlen erfinden):
 * Solange fuer einen Netzbetreiber KEIN Preisblatt hinterlegt ist, rechnen wir
 * weiterhin mit der groben Regionalpauschale nach 2-stelliger PLZ. Das Ergebnis
 * traegt dann `herkunft: 'schaetzung'`, damit die Oberflaeche es als Schaetzung
 * kennzeichnen kann — statt eine Pauschale mit vier Nachkommastellen als den
 * Tarif genau dieses Netzbetreibers auszugeben.
 *
 * QUELLE DER WAHRHEIT IST DER SERVER (GET /api/netzentgelte).
 *
 * Architektur-Hinweis (identisch zu strukturaufschlag.js geloest):
 * Die Rechen-Engine (kalkulationEngine.js) ist SYNCHRON und kann nicht auf den
 * Server warten. Deshalb:
 *   1. ladeNetzentgelte() / ensureNetzentgelte() holt die Preisblaetter EINMAL
 *      vom Server (Aufrufer: useKalkulator, Admin-Seite).
 *   2. Das Ergebnis landet in einem Modul-Level-Cache (setNetzentgelte).
 *   3. findePreisblatt() und berechneNetzentgelt() lesen SYNCHRON aus diesem Cache.
 *   4. Solange nichts geladen ist, gilt die bisherige Regionalpauschale.
 * Ob Preisblaetter geladen sind, verraet getNetzentgelteStatus().
 */

import { apiFetch } from '@/lib/api';

const ENDPOINT = '/api/netzentgelte';

/** Uebliche Benutzungsstundengrenze, wenn das Preisblatt keine eigene nennt. */
export const DEFAULT_BENUTZUNGSSTUNDEN_GRENZE = 2500;

// ============================================================
// Modul-Level-Cache (die einzige Quelle fuer den synchronen Zugriff)
// ============================================================
let preisblaetter = [];        // normalisierte Zeilen vom Server
let preisblattIndex = new Map(); // "name|ebene" -> Zeilen, neueste gueltig_ab zuerst
let loadedAt = null;           // Zeitpunkt des letzten erfolgreichen Ladens
let source = 'default';        // 'default' (nichts geladen) | 'server' | 'unavailable'
let loadError = null;          // ehrlicher Fehlerstring, wenn der Server nicht erreichbar war
let isLoading = false;
let loadPromise = null;        // laufende Anfrage (Dedup)

// ============================================================
// Hilfsfunktionen
// ============================================================

/** NUMERIC-Spalten kommen von node-pg als String zurueck. */
const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
  return Number.isFinite(n) ? n : null;
};

const toIntOrNull = (value) => {
  const n = toNumberOrNull(value);
  return n === null ? null : Math.round(n);
};

const round2 = (n) => Math.round(n * 100) / 100;
const round4 = (n) => Math.round(n * 10000) / 10000;

/**
 * Kalendertag (YYYY-MM-DD) aus den LOKALEN Bestandteilen.
 * toISOString() rechnet nach UTC um und verschiebt oestlich von UTC auf den
 * Vortag — genau der Fehler, der in diesem Projekt schon zweimal passiert ist.
 */
const heute = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * Bringt einen Datumswert auf 'YYYY-MM-DD'. Der Server liefert DATE-Spalten
 * bereits per to_char in dieser Form; ein durchgerutschter ISO-Zeitstempel wird
 * hier abgeschnitten, statt ihn erneut durch new Date() zu schicken.
 */
const toDateOnly = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const p = (n) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${p(value.getMonth() + 1)}-${p(value.getDate())}`;
  }
  const s = String(value).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
};

/**
 * Vergleichsform eines Netzbetreibernamens.
 *
 * Toleriert AUSSCHLIESSLICH deterministische Schreibvarianten:
 *   - Gross-/Kleinschreibung
 *   - Leerzeichen (auch geschuetzte) und Mehrfach-Leerzeichen
 *   - Umlaut-Umschrift ae/oe/ue/ss (im Projekt werden Umlaute so geschrieben,
 *     in importierten Preisblaettern stehen sie oft als echte Umlaute)
 *
 * NICHT gekuerzt werden Rechtsform oder Namensbestandteile: "Netze BW GmbH"
 * und "Netze BW Regional GmbH" bleiben verschiedene Netzbetreiber. Im Zweifel
 * lieber kein Treffer (-> Schaetzung, sichtbar gekennzeichnet) als der Tarif
 * eines fremden Netzbetreibers.
 */
export function normalisiereNetzbetreiberName(name) {
  if (name === null || name === undefined) return '';
  // Unicode-Escapes statt literaler Umlaute — im Projekt werden Umlaute im
  // Code als ae/oe/ue geschrieben.
  return String(name)
    .replace(/[\u00a0\u2007\u202f\u2009\t]/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\u00e4/g, 'ae')
    .replace(/\u00f6/g, 'oe')
    .replace(/\u00fc/g, 'ue')
    .replace(/\u00df/g, 'ss')
    .replace(/\s+/g, ' ');
}

/** Spannungsebene in Vergleichsform (Default NS). Es wird EXAKT verglichen. */
export function normalisiereSpannungsebene(ebene) {
  const s = String(ebene ?? '').trim().toUpperCase();
  return s || 'NS';
}

const indexKey = (name, ebene) =>
  `${normalisiereNetzbetreiberName(name)}|${normalisiereSpannungsebene(ebene)}`;

/**
 * Eine Server-Zeile in die Form bringen, mit der gerechnet wird.
 * Alle Geldbetraege werden zu echten Zahlen (oder null — NIE zu 0, denn
 * "nicht gepflegt" ist etwas anderes als "kostet nichts").
 */
export function normalisierePreisblatt(row) {
  if (!row) return null;
  const netzbetreiber = row.netzbetreiber ? String(row.netzbetreiber).trim() : '';
  if (!netzbetreiber) return null;

  return {
    id: row.id ?? null,
    netzbetreiber,
    spannungsebene: normalisiereSpannungsebene(row.spannungsebene),
    gueltig_ab: toDateOnly(row.gueltig_ab),
    gueltig_bis: toDateOnly(row.gueltig_bis),

    // SLP
    arbeitspreis_ct_kwh: toNumberOrNull(row.arbeitspreis_ct_kwh),
    grundpreis_eur_jahr: toNumberOrNull(row.grundpreis_eur_jahr),

    // RLM unter der Benutzungsstundengrenze
    leistungspreis_eur_kw_jahr: toNumberOrNull(row.leistungspreis_eur_kw_jahr),
    arbeitspreis_rlm_ct_kwh: toNumberOrNull(row.arbeitspreis_rlm_ct_kwh),

    // RLM ab der Benutzungsstundengrenze (Hochlast)
    leistungspreis_hoch_eur_kw_jahr: toNumberOrNull(row.leistungspreis_hoch_eur_kw_jahr),
    arbeitspreis_hoch_ct_kwh: toNumberOrNull(row.arbeitspreis_hoch_ct_kwh),

    benutzungsstunden_grenze: toIntOrNull(row.benutzungsstunden_grenze) ?? DEFAULT_BENUTZUNGSSTUNDEN_GRENZE,
    messstellenbetrieb_eur_jahr: toNumberOrNull(row.messstellenbetrieb_eur_jahr),

    quelle: row.quelle ? String(row.quelle) : null,
    updated_at: row.updated_at || null,
  };
}

/**
 * Setzt den Modul-Cache (nach dem Laden vom Server oder nach einem Import).
 * @param {Array} rows - Preisblatt-Zeilen wie vom API-Vertrag beschrieben
 * @returns {number} Anzahl uebernommener Preisblaetter
 */
export function setNetzentgelte(rows, meta = {}) {
  const liste = Array.isArray(rows) ? rows : [];
  preisblaetter = liste.map(normalisierePreisblatt).filter(Boolean);

  const idx = new Map();
  preisblaetter.forEach((pb) => {
    const key = indexKey(pb.netzbetreiber, pb.spannungsebene);
    if (!idx.has(key)) idx.set(key, []);
    idx.get(key).push(pb);
  });
  // Neuestes gueltig_ab zuerst; Zeilen ohne gueltig_ab ans Ende
  idx.forEach((arr) => {
    arr.sort((a, b) => {
      if (a.gueltig_ab === b.gueltig_ab) return 0;
      if (!a.gueltig_ab) return 1;
      if (!b.gueltig_ab) return -1;
      return a.gueltig_ab < b.gueltig_ab ? 1 : -1;
    });
  });
  preisblattIndex = idx;

  loadedAt = meta.loaded_at || new Date().toISOString();
  source = meta.source || 'server';
  loadError = null;
  return preisblaetter.length;
}

/** Alle bekannten Preisblaetter (Kopie), z.B. fuer eine Verwaltungsansicht. */
export function getPreisblaetter() {
  return preisblaetter.map((pb) => ({ ...pb }));
}

/**
 * Ehrlicher Zustand des Caches — damit die Oberflaeche nicht so tut, als waere
 * eine Regionalpauschale das gepflegte Preisblatt dieses Netzbetreibers.
 * @returns {{loaded:boolean, loading:boolean, error:string|null, source:string,
 *            anzahl:number, netzbetreiber_anzahl:number, loaded_at:string|null}}
 */
export function getNetzentgelteStatus() {
  return {
    loaded: source === 'server' && !loadError,
    loading: isLoading,
    error: loadError,
    source,
    anzahl: preisblaetter.length,
    netzbetreiber_anzahl: preisblattIndex.size,
    loaded_at: loadedAt,
  };
}

/**
 * Holt die Preisblaetter vom Server und schreibt sie in den Modul-Cache.
 * @returns {Promise<{ok:boolean, anzahl:number, status:object, error?:string}>}
 */
export function ladeNetzentgelte({ force = false } = {}) {
  if (loadPromise && !force) return loadPromise;

  isLoading = true;
  loadPromise = (async () => {
    try {
      const resp = await apiFetch(ENDPOINT);
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try {
          const body = await resp.json();
          if (body?.error) msg = body.error;
        } catch { /* Body ist kein JSON */ }
        throw new Error(msg);
      }
      const data = await resp.json();
      const rows = Array.isArray(data) ? data : (data?.rows || data?.items || []);
      const anzahl = setNetzentgelte(rows, { source: 'server' });
      return { ok: true, anzahl, status: getNetzentgelteStatus() };
    } catch (err) {
      // KEIN stiller Fallback: der Zustand wird ehrlich als "nicht geladen"
      // markiert. Gerechnet wird dann mit der Regionalpauschale, und das
      // Ergebnis traegt herkunft: 'schaetzung'.
      preisblaetter = [];
      preisblattIndex = new Map();
      loadedAt = null;
      source = 'unavailable';
      loadError = 'Netzentgelte konnten nicht vom Server geladen werden: ' + err.message;
      return { ok: false, anzahl: 0, status: getNetzentgelteStatus(), error: loadError };
    } finally {
      isLoading = false;
    }
  })();

  return loadPromise;
}

/**
 * Stellt sicher, dass die Preisblaetter geladen sind (dedupliziert parallele
 * Aufrufe). Nach einem Fehler wird erneut versucht.
 */
export function ensureNetzentgelte() {
  if (loadPromise) {
    return loadPromise.then((res) => (res.ok ? res : ladeNetzentgelte({ force: true })));
  }
  return ladeNetzentgelte();
}

/**
 * SYNCHRONE Suche im Modul-Cache: das am Stichtag gueltige Preisblatt.
 *
 * Gueltig ist eine Zeile, wenn gueltig_ab <= Stichtag und (gueltig_bis fehlt
 * oder gueltig_bis >= Stichtag). Von mehreren gueltigen Zeilen gewinnt die mit
 * dem spaetesten gueltig_ab.
 *
 * Der Netzbetreibername wird tolerant, aber NICHT unscharf verglichen
 * (siehe normalisiereNetzbetreiberName) — im Zweifel kein Treffer.
 *
 * @param {string} netzbetreiber
 * @param {string} [spannungsebene='NS']
 * @param {string|Date} [stichtag] - Default: heute
 * @returns {object|null}
 */
export function findePreisblatt(netzbetreiber, spannungsebene = 'NS', stichtag) {
  if (!netzbetreiber) return null;
  const liste = preisblattIndex.get(indexKey(netzbetreiber, spannungsebene));
  if (!liste || liste.length === 0) return null;

  const tag = toDateOnly(stichtag) || heute();
  for (const pb of liste) {
    if (pb.gueltig_ab && pb.gueltig_ab > tag) continue;
    if (pb.gueltig_bis && pb.gueltig_bis < tag) continue;
    return pb;
  }
  return null;
}

/**
 * Gibt es fuer diesen Netzbetreiber/diese Spannungsebene ueberhaupt
 * Preisblaetter (egal welcher Stichtag)? Dient nur einem praezisen Hinweistext.
 */
export function hatPreisblaetter(netzbetreiber, spannungsebene = 'NS') {
  const liste = preisblattIndex.get(indexKey(netzbetreiber, spannungsebene));
  return !!(liste && liste.length);
}

/**
 * Vollstaendiges Ergebnisobjekt. Wird IMMER in dieser Form zurueckgegeben,
 * damit die Aufrufer nie zwischen zwei Formen unterscheiden muessen.
 */
function ergebnis({
  netzentgelt_ct_kwh = null,
  herkunft = 'schaetzung',
  quelle = null,
  benutzungsstunden = null,
  leistungspreis_anteil_ct_kwh = null,
  hinweis = null,
  extra = {},
}) {
  return {
    netzentgelt_ct_kwh,
    herkunft,                       // 'preisblatt' | 'schaetzung'
    quelle,
    benutzungsstunden,
    leistungspreis_anteil_ct_kwh,
    hinweis,
    // Zusatzangaben fuer die Anzeige (nicht Teil des Pflichtvertrags)
    hochlast: extra.hochlast ?? null,
    benutzungsstunden_grenze: extra.benutzungsstunden_grenze ?? null,
    jahreskosten_eur: extra.jahreskosten_eur ?? null,
    preisblatt_id: extra.preisblatt_id ?? null,
    gueltig_ab: extra.gueltig_ab ?? null,
    netzbetreiber: extra.netzbetreiber ?? null,
    spannungsebene: extra.spannungsebene ?? null,
  };
}

/**
 * DIE VERBINDLICHE NETZENTGELT-FORMEL.
 *
 * A) OHNE Preisblatt -> Regionalpauschale, herkunft 'schaetzung'
 * B) MIT Preisblatt, RLM und leistung > 0 -> zweiteiliger Tarif mit
 *    Benutzungsstundenregelung, herkunft 'preisblatt'
 * C) MIT Preisblatt und SLP -> Arbeitspreis + Grundpreis + Messstellenbetrieb,
 *    herkunft 'preisblatt'
 * Fehlende Werte im Preisblatt fuehren IMMER zurueck auf Fall A, kenntlich
 * gemacht ueber herkunft 'schaetzung' + hinweis.
 *
 * @param {object} params
 * @param {string} params.netzbetreiberName
 * @param {string} [params.spannungsebene='NS']
 * @param {number} params.verbrauch - kWh/a
 * @param {number} [params.leistung=0] - Jahreshoechstleistung in kW (nur RLM)
 * @param {string} [params.zaehlverfahren='SLP'] - 'SLP' | 'RLM'
 * @param {number} params.pauschale_ct_kwh - Regionalpauschale als Rueckfallebene
 * @param {string|Date} [params.stichtag] - Default: heute
 * @returns {{netzentgelt_ct_kwh:number|null, herkunft:string, quelle:string|null,
 *            benutzungsstunden:number|null, leistungspreis_anteil_ct_kwh:number|null,
 *            hinweis:string|null}}
 */
export function berechneNetzentgelt({
  netzbetreiberName,
  spannungsebene = 'NS',
  verbrauch,
  leistung = 0,
  zaehlverfahren = 'SLP',
  pauschale_ct_kwh,
  stichtag,
} = {}) {
  const ebene = normalisiereSpannungsebene(spannungsebene);
  const verbrauchNum = toNumberOrNull(verbrauch);
  const leistungNum = toNumberOrNull(leistung);
  const pauschale = toNumberOrNull(pauschale_ct_kwh);
  const istRLM = String(zaehlverfahren ?? '').trim().toUpperCase() === 'RLM';

  const hatVerbrauch = verbrauchNum !== null && verbrauchNum > 0;
  const hatLeistung = leistungNum !== null && leistungNum > 0;
  const benutzungsstunden = hatVerbrauch && hatLeistung
    ? round2(verbrauchNum / leistungNum)
    : null;

  const basis = {
    netzbetreiber: netzbetreiberName || null,
    spannungsebene: ebene,
    benutzungsstunden_grenze: null,
  };

  // Fall A — die Rueckfallebene. Wird von allen anderen Faellen benutzt,
  // sobald echte Werte fehlen. Traegt IMMER herkunft 'schaetzung'.
  const schaetzung = (hinweis) => ergebnis({
    netzentgelt_ct_kwh: pauschale === null ? null : round4(pauschale),
    herkunft: 'schaetzung',
    quelle: null,
    benutzungsstunden,
    leistungspreis_anteil_ct_kwh: null,
    hinweis: pauschale === null
      ? 'Kein Preisblatt und keine Regionalpauschale verfuegbar — Netzentgelt unbekannt.'
      : hinweis,
    extra: basis,
  });

  const preisblatt = findePreisblatt(netzbetreiberName, ebene, stichtag);

  // ---- Fall A: kein Preisblatt ----
  if (!preisblatt) {
    const status = getNetzentgelteStatus();
    let hinweis;
    if (status.error) {
      hinweis = 'Preisblaetter nicht geladen (' + status.error + ') — Regionalpauschale.';
    } else if (!netzbetreiberName) {
      hinweis = 'Kein Netzbetreiber ermittelt — Regionalpauschale nach PLZ-Region.';
    } else if (hatPreisblaetter(netzbetreiberName, ebene)) {
      hinweis = `Fuer ${netzbetreiberName} (${ebene}) liegt kein am ${toDateOnly(stichtag) || heute()} gueltiges Preisblatt vor — Regionalpauschale.`;
    } else {
      hinweis = `Kein Preisblatt fuer ${netzbetreiberName} (${ebene}) hinterlegt — Regionalpauschale nach PLZ-Region.`;
    }
    return schaetzung(hinweis);
  }

  const gemeinsam = {
    ...basis,
    benutzungsstunden_grenze: preisblatt.benutzungsstunden_grenze,
    preisblatt_id: preisblatt.id,
    gueltig_ab: preisblatt.gueltig_ab,
  };
  const messstellenbetrieb = preisblatt.messstellenbetrieb_eur_jahr || 0;

  // Ohne Verbrauch laesst sich aus Jahreskosten kein ct/kWh bilden
  // (Division durch Null). Ehrlich als Schaetzung kennzeichnen.
  if (!hatVerbrauch) {
    return schaetzung('Kein Jahresverbrauch angegeben — Preisblatt kann nicht in ct/kWh umgerechnet werden.');
  }

  // ---- Fall B: RLM mit Leistung ----
  if (istRLM) {
    if (!hatLeistung) {
      // Ohne Jahreshoechstleistung kein zweiteiliger Tarif. Der SLP-Arbeitspreis
      // waere hier deutlich zu hoch — also lieber sichtbar schaetzen.
      return schaetzung('RLM ohne Jahreshoechstleistung (kW) — Leistungspreis nicht berechenbar, Regionalpauschale.');
    }

    const grenze = preisblatt.benutzungsstunden_grenze ?? DEFAULT_BENUTZUNGSSTUNDEN_GRENZE;
    const stundenRoh = verbrauchNum / leistungNum;
    const hochlast = stundenRoh >= grenze;

    const LP = hochlast ? preisblatt.leistungspreis_hoch_eur_kw_jahr : preisblatt.leistungspreis_eur_kw_jahr;
    const AP = hochlast ? preisblatt.arbeitspreis_hoch_ct_kwh : preisblatt.arbeitspreis_rlm_ct_kwh;

    if (LP === null || AP === null) {
      return schaetzung('Preisblatt ohne RLM-Werte');
    }

    const jahreskosten = LP * leistungNum + (AP / 100) * verbrauchNum + messstellenbetrieb;
    const ctKwh = (jahreskosten / verbrauchNum) * 100;
    const lpAnteil = (LP * leistungNum) / verbrauchNum * 100;

    return ergebnis({
      netzentgelt_ct_kwh: round4(ctKwh),
      herkunft: 'preisblatt',
      quelle: preisblatt.quelle,
      benutzungsstunden,
      leistungspreis_anteil_ct_kwh: round4(lpAnteil),
      hinweis: null,
      extra: { ...gemeinsam, hochlast, jahreskosten_eur: round2(jahreskosten) },
    });
  }

  // ---- Fall C: SLP ----
  if (preisblatt.arbeitspreis_ct_kwh === null) {
    return schaetzung('Preisblatt ohne SLP-Arbeitspreis');
  }

  const grundpreis = preisblatt.grundpreis_eur_jahr || 0;
  const jahreskostenSlp = (preisblatt.arbeitspreis_ct_kwh / 100) * verbrauchNum + grundpreis + messstellenbetrieb;
  const ctKwhSlp = (jahreskostenSlp / verbrauchNum) * 100;

  return ergebnis({
    netzentgelt_ct_kwh: round4(ctKwhSlp),
    herkunft: 'preisblatt',
    quelle: preisblatt.quelle,
    benutzungsstunden,
    // SLP kennt keinen Leistungspreis — der Anteil ist echt 0, nicht "unbekannt".
    leistungspreis_anteil_ct_kwh: 0,
    hinweis: null,
    extra: { ...gemeinsam, hochlast: false, jahreskosten_eur: round2(jahreskostenSlp) },
  });
}
